<script setup lang="ts">
const platformStore = usePlatformStore();
type Props = {
  strokeColor?: string;
  logoType?: "primary" | "lightMode";
};
const { strokeColor, logoType = "primary" } = defineProps<Props>();

const logo = computed(() => {
  return (
    platformStore.getPlatformConfiguration?.assets?.[
      logoType === "primary" ? "primaryLogo" : "lightModeLogo"
    ] || platformStore.getPlatformConfiguration?.assets?.["primaryLogo"]
  );
});

// Use the platform configuration as a fallback, instead of always black
const platformStrokeColor = computed(() => {
  return (
    strokeColor ||
    platformStore.getPlatformConfiguration?.styling.theme["color-1"] ||
    "black"
  );
});
</script>

<template>
  <div
    class="loading-spinner w-browser"
    data-cy="loading-spinner"
    data-test="loading-spinner"
  >
    <slot
      v-if="logo"
      name="logo"
    >
      <img
        class="loading-spinner__image"
        :src="logo"
        alt="loading spinner logo"
      />
    </slot>
    <svg
      class="loading-spinner__spinner"
      viewBox="25 25 50 50"
    >
      <circle
        class="loading-spinner__spinner__path"
        :style="{
          stroke: platformStrokeColor,
        }"
        cx="50"
        cy="50"
        r="20"
        fill="none"
        stroke-width="1"
        stroke-miterlimit="10"
      />
    </svg>
  </div>
</template>

<style lang="postcss" scoped>
.loading-spinner {
  @apply relative flex flex-col items-center justify-center;
  @apply pt-14 h-96;
  animation: fadeIn linear 0.8s;

  &__image {
    width: 100px;
    height: 100px;
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;
    @apply p-2 object-contain;
  }

  &__spinner {
    height: 200px;
    width: 200px;
    animation: rotate 3s linear infinite;
    transform-origin: center center;
    position: absolute;
    top: 60px;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;

    &__path {
      stroke-dasharray: 1, 200;
      stroke-dashoffset: 0;
      animation: dash 3s ease-in-out infinite;
      stroke-linecap: round;
    }
  }
}

@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  80% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 200;
    stroke-dashoffset: 0;
  }

  50% {
    stroke-dasharray: 89, 200;
    stroke-dashoffset: -35px;
  }

  100% {
    stroke-dasharray: 89, 200;
    stroke-dashoffset: -124px;
  }
}
</style>
